/**
 * Dialog for cloning a table to a new table on the same or different database/connection.
 * Supports MSSQL, PostgreSQL, and SQLite (same-engine only).
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { Copy, Table2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toast } from '../../../components/ui/toaster';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { applyCloneTargetConnectionChange } from '../utils/cloneTargetConnectionChange';
import { useProFeature, UpgradeDialog } from '../../../core/licensing';
import { PersonalModeCloneTableLimitMessage } from './PersonalModeCloneTableLimitMessage';
import {
  checkPersonalModeLimits,
  fetchTableRowCount,
  type PersonalModeLimitCheck,
} from '../utils/personalModeLimits';
import { validateTableName, validateSchemaName } from '../utils/validation';
import { useClonePreviewScript } from '../hooks/useClonePreviewScript';
import { CloneScriptPreviewDialog } from './CloneScriptPreviewDialog';
import { useDatabaseList } from '../../database-objects/hooks/useDatabaseList';
import { FallbackSelectContent } from '../../connection-diagnostics/components/FallbackSelectContent';
import { getTables } from '../../database-objects/services/objectsService';
import type { DatabaseEngineId } from '../../../core/engines/engineTypes';
import type { CloneTableConstraintOptions, CloneTableRequest } from '../types/cloneTable';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { safeConnectionName } from '@/types/safeConnectionDisplay';
type CloneStartedParams = {
  operationId: string;
  sourceTableName: string;
  targetTableName: string;
  targetConnectionId: string;
  targetDatabaseName: string;
  targetSchema: string;
};

type CloneTableDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  databaseName: string;
  schema: string;
  tableName: string;
  onCloneStarted?: (params: CloneStartedParams) => void;
};

type CloneFormValues = {
  targetConnectionId: string;
  targetDatabaseName: string;
  targetSchema: string;
  targetTableName: string;
  includeData: boolean;
  includeConstraints: boolean;
  constraintOptions: CloneTableConstraintOptions;
};

export function CloneTableDialog({
  open,
  onOpenChange,
  connectionId,
  databaseName,
  schema,
  tableName,
  onCloneStarted,
}: CloneTableDialogProps) {
  const { connections, activeConnections } = useConnectionStore();
  const [isCloning, setIsCloning] = useState(false);
  const { previewState, requestPreview, setPreviewOpen } = useClonePreviewScript();

  // License check for clone with data
  const { canUse: canUseFullClone } = useProFeature('clone-table-full');
  const [isCheckingRowCount, setIsCheckingRowCount] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [limitCheck, setLimitCheck] = useState<PersonalModeLimitCheck | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // State for target schemas (extracted from tables)
  const [schemas, setSchemas] = useState<string[]>([]);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(false);
  const [schemasError, setSchemasError] = useState<string | undefined>();

  // Get source connection details
  const sourceConnection = useMemo(() => {
    const saved = connections.find((c) => c.id === connectionId);
    if (saved) return saved;
    return activeConnections.find((c) => c.id === connectionId)?.connection;
  }, [connections, activeConnections, connectionId]);

  const sourceEngine = (sourceConnection?.engine || 'mssql') as DatabaseEngineId;
  const isSqlite = sourceEngine === 'sqlite';
  const defaultSchema = sourceEngine === 'pgsql' ? 'public' : 'dbo';

  // Filter connections to same engine only
  const sameEngineConnections = useMemo(() => {
    const activeWithConfigs = activeConnections
      .map((ac) => {
        const config = connections.find((c) => c.id === ac.id) || ac.connection;
        return { ...ac, connection: config };
      })
      .filter((ac) => {
        const engine = ac.connection.engine || 'mssql';
        return engine === sourceEngine && ac.status !== 'disconnected';
      });

    return activeWithConfigs;
  }, [activeConnections, connections, sourceEngine]);

  const form = useForm({
    defaultValues: {
      targetConnectionId: connectionId,
      targetDatabaseName: databaseName,
      targetSchema: schema || defaultSchema,
      targetTableName: `${tableName}_clone`,
      includeData: false,
      includeConstraints: true,
      constraintOptions: {
        primaryKey: true,
        foreignKeys: true,
        uniqueConstraints: true,
        checkConstraints: true,
        indexes: true,
      },
    } as CloneFormValues,
    onSubmit: async ({ value }) => {
      await handleClone(value);
    },
  });

  const targetConnectionId = useStore(
    form.store,
    (state) => state.values.targetConnectionId
  );
  const targetDatabaseName = useStore(
    form.store,
    (state) => state.values.targetDatabaseName
  );
  const targetSchema = useStore(
    form.store,
    (state) => state.values.targetSchema
  );
  const targetTableName = useStore(
    form.store,
    (state) => state.values.targetTableName
  );
  const includeConstraints = useStore(
    form.store,
    (state) => state.values.includeConstraints
  );
  // Check if this is a cross-database clone
  const isCrossDatabase = targetDatabaseName !== databaseName || targetConnectionId !== connectionId;

  // Target databases from the SHARED per-connection listing the Object Explorer
  // already fills (`useDatabaseList`): choosing a target connection the app has
  // listed fills the picker immediately. `databases` is `[]` unless
  // `state === 'ok'`, so the engine's hardcoded fallback list is no longer
  // rendered as if it were a real listing (Slice 5b — this picker used to render
  // `result.databases` verbatim); the failure case gets the shared affordance
  // instead of a free-text line in a disabled row.
  const {
    databases,
    state: databasesState,
    isInitialLoading: isLoadingDatabases,
  } = useDatabaseList({
    connectionId: targetConnectionId || undefined,
    engine: sourceEngine,
    enabled: !!targetConnectionId && !isSqlite,
  });

  // Fetch schemas when target database changes
  useEffect(() => {
    if (!targetConnectionId || !targetDatabaseName || isSqlite) {
      setSchemas([]);
      setSchemasError(undefined);
      return;
    }

    let cancelled = false;

    async function fetchSchemas() {
      setIsLoadingSchemas(true);
      setSchemasError(undefined);

      try {
        // Get tables to extract unique schemas
        const result = await getTables(targetConnectionId, targetDatabaseName, sourceEngine);
        if (cancelled) return;

        if (result.error) {
          setSchemasError(result.error);
          // Still try to extract schemas if we got partial data
          if (result.tables.length === 0) {
            // Fallback to common default schemas
            setSchemas(sourceEngine === 'pgsql' ? ['public'] : ['dbo']);
            return;
          }
        }

        // Extract unique schemas from tables
        const uniqueSchemas = Array.from(new Set(result.tables.map((t) => t.schema))).sort();
        setSchemas(uniqueSchemas);
      } catch (err) {
        if (cancelled) return;
        setSchemasError(err instanceof Error ? err.message : 'Failed to load schemas');
        // Fallback to common default schemas
        setSchemas(sourceEngine === 'pgsql' ? ['public'] : ['dbo']);
      } finally {
        if (!cancelled) {
          setIsLoadingSchemas(false);
        }
      }
    }

    fetchSchemas();

    return () => {
      cancelled = true;
    };
  }, [targetConnectionId, targetDatabaseName, sourceEngine, isSqlite]);

  const tableNameError = useMemo(
    () => validateTableName(targetTableName, sourceEngine),
    [targetTableName, sourceEngine]
  );

  const schemaError = useMemo(
    () => validateSchemaName(targetSchema, sourceEngine),
    [targetSchema, sourceEngine]
  );

  const isFormValid = useMemo(() => {
    return !tableNameError && !schemaError;
  }, [tableNameError, schemaError]);

  /**
   * The one place a `CloneTableRequest` is assembled — both Clone and Preview
   * Script go through it, so the preview always describes the same operation.
   */
  const buildCloneRequest = useCallback(
    (values: CloneFormValues): CloneTableRequest => ({
      sourceConnectionId: connectionId,
      sourceDatabaseName: databaseName,
      sourceSchema: schema,
      sourceTableName: tableName,
      engine: sourceEngine,
      targetConnectionId: values.targetConnectionId,
      targetDatabaseName: values.targetDatabaseName,
      targetSchema: values.targetSchema,
      targetTableName: values.targetTableName,
      includeData: values.includeData,
      includeConstraints: values.includeConstraints,
      constraintOptions: values.includeConstraints ? values.constraintOptions : undefined,
    }),
    [connectionId, databaseName, schema, tableName, sourceEngine]
  );

  const handleClone = async (values: CloneFormValues) => {
    if (!isFormValid) {
      toast.error({
        title: 'Validation Error',
        description: tableNameError || schemaError || 'Invalid form',
      });
      return;
    }

    if (!window.electronAPI?.cloneTable) {
      toast.error({
        title: 'Error',
        description: 'Clone Table API is not available',
      });
      return;
    }

    // License check: if including data and not Pro, check row count
    if (values.includeData && !canUseFullClone) {
      setIsCheckingRowCount(true);
      try {
        const rowResult = await fetchTableRowCount(connectionId, databaseName, schema, tableName);

        if (rowResult.success && rowResult.rowCount !== undefined && rowResult.rowCount !== null) {
          const check = checkPersonalModeLimits(rowResult.rowCount);
          if (check.exceedsLimit) {
            setLimitCheck(check);
            setShowLimitMessage(true);
            setIsCheckingRowCount(false);
            return;
          }
        }
        // If row count check fails or count is within limit, proceed
      } catch (error) {
        console.error('[CloneTableDialog] Row count check error:', error);
        // On error, allow the operation to proceed (fail open)
      } finally {
        setIsCheckingRowCount(false);
      }
    }

    setIsCloning(true);

    try {
      // Check if target table exists first
      const existsResult = await window.electronAPI.cloneTable.checkExists({
        engine: sourceEngine,
        connectionId: values.targetConnectionId,
        databaseName: values.targetDatabaseName,
        schema: values.targetSchema,
        tableName: values.targetTableName,
      });

      if (existsResult.exists) {
        toast.error({
          title: 'Table Already Exists',
          description: `A table named "${values.targetTableName}" already exists in the target location.`,
        });
        setIsCloning(false);
        return;
      }

      // Start the clone operation
      const result = await window.electronAPI.cloneTable.start(buildCloneRequest(values));

      if (!result.success || !result.operationId) {
        toast.error({
          title: 'Clone Failed',
          description: result.error || 'Failed to start clone operation',
        });
        setIsCloning(false);
        return;
      }

      // Close this dialog and open progress dialog
      onOpenChange(false);
      form.reset();

      // Notify parent to show progress dialog
      if (onCloneStarted) {
        onCloneStarted({
          operationId: result.operationId,
          sourceTableName: tableName,
          targetTableName: values.targetTableName,
          targetConnectionId: values.targetConnectionId,
          targetDatabaseName: values.targetDatabaseName,
          targetSchema: values.targetSchema,
        });
      }
    } catch (err) {
      toast.error({
        title: 'Clone Failed',
        description: err instanceof Error ? err.message : 'Unknown error occurred',
      });
    } finally {
      setIsCloning(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isCloning) {
      form.reset();
      setShowLimitMessage(false);
      setLimitCheck(null);
      onOpenChange(false);
    }
  }, [isCloning, form, onOpenChange]);

  // Reset limit state when dialog opens
  useEffect(() => {
    if (open) {
      setShowLimitMessage(false);
      setLimitCheck(null);
    }
  }, [open]);

  // Handler for continuing with schema-only clone
  const handleContinueSchemaOnly = useCallback(() => {
    form.setFieldValue('includeData', false);
    setShowLimitMessage(false);
    setLimitCheck(null);
  }, [form]);

  // Handler for upgrade button
  const handleUpgrade = useCallback(() => {
    setShowUpgradeDialog(true);
  }, []);

  const handlePreview = useCallback(() => {
    void requestPreview(buildCloneRequest(form.state.values));
  }, [form, buildCloneRequest, requestPreview]);

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent
          data-testid="clone-table-dialog"
          className="sm:max-w-[750px]"
        >
          {/* Show limit message when Personal mode and large table */}
          {showLimitMessage && limitCheck ? (
            <PersonalModeCloneTableLimitMessage
              limitCheck={limitCheck}
              onUpgrade={handleUpgrade}
              onContinueSchemaOnly={handleContinueSchemaOnly}
              onCancel={handleClose}
            />
          ) : isCheckingRowCount ? (
            <div className="flex flex-col items-center justify-center p-8 gap-3">
              <RandomDotmSpinner size={32} className="text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Checking table row count...</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                    <Copy className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <DialogTitle>Clone Table</DialogTitle>
                    <DialogDescription className="mt-1">
                      Create a copy of &quot;{tableName}&quot;
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <form
          id="clone-table-form"
          data-testid="clone-table-form"
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <div className="py-4 grid grid-cols-2 gap-6">
            {/* Left column - Destination settings */}
            <div className="space-y-4">
              {/* Source table (read-only) */}
              <div className="space-y-2">
                <Label>Source Table</Label>
                <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md">
                  <Table2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {isSqlite ? tableName : `${schema}.${tableName}`}
                    <span className="text-muted-foreground ml-2">
                      ({databaseName})
                    </span>
                  </span>
                </div>
              </div>

              {/* Target connection */}
              {!isSqlite && (
                <div className="space-y-2">
                  <Label htmlFor="target-connection">Target Connection</Label>
                  <form.Field name="targetConnectionId">
                    {(field) => (
                      <Select
                        value={field.state.value}
                        onValueChange={(value: string) => {
                          field.handleChange(value);
                          applyCloneTargetConnectionChange(
                            form,
                            value,
                            connections,
                            activeConnections,
                            defaultSchema
                          );
                        }}
                      >
                        <SelectTrigger
                          id="target-connection"
                          data-testid="clone-target-connection"
                        >
                          <SelectValue placeholder="Select connection..." />
                        </SelectTrigger>
                        <SelectContent>
                          {sameEngineConnections.map((conn) => (
                            <SelectItem key={conn.id} value={conn.id}>
                              {safeConnectionName(conn.connection)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                  <p className="text-xs text-muted-foreground">
                    Only {sourceEngine.toUpperCase()} connections are shown
                  </p>
                </div>
              )}

              {/* Target database */}
              {!isSqlite && (
                <div className="space-y-2">
                  <Label htmlFor="target-database">Target Database</Label>
                  <form.Field name="targetDatabaseName">
                    {(field) => (
                      <Select
                        value={field.state.value}
                        onValueChange={(value: string) => {
                          field.handleChange(value);
                          form.setFieldValue('targetSchema', defaultSchema);
                        }}
                        disabled={isLoadingDatabases}
                      >
                        <SelectTrigger
                          id="target-database"
                          data-testid="clone-target-database"
                        >
                          {isLoadingDatabases ? (
                            <span className="flex items-center gap-2 text-muted-foreground">
                              <RandomDotmSpinner size={12} />
                              Loading...
                            </span>
                          ) : (
                            <SelectValue placeholder="Select database..." />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <FallbackSelectContent
                            connectionId={targetConnectionId || undefined}
                            state={databasesState}
                            databases={databases}
                            isLoading={isLoadingDatabases}
                            selectedDatabase={field.state.value}
                            testIdPrefix="clone-target"
                          />
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>
              )}

              {/* Target schema */}
              {!isSqlite && (
                <div className="space-y-2">
                  <Label htmlFor="target-schema">Target Schema</Label>
                  <form.Field name="targetSchema">
                    {(field) => (
                      <Select
                        value={field.state.value}
                        onValueChange={(value: string) => field.handleChange(value)}
                        disabled={isLoadingSchemas || !targetDatabaseName}
                      >
                        <SelectTrigger
                          id="target-schema"
                          data-testid="clone-target-schema"
                        >
                          {isLoadingSchemas ? (
                            <span className="flex items-center gap-2 text-muted-foreground">
                              <RandomDotmSpinner size={12} />
                              Loading...
                            </span>
                          ) : (
                            <SelectValue placeholder="Select schema..." />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {schemas.length === 0 && !isLoadingSchemas ? (
                            <SelectItem value="_none" disabled>
                              {schemasError || 'No schemas found'}
                            </SelectItem>
                          ) : (
                            schemas.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s || '(default)'}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                  {schemaError && (
                    <p className="text-xs text-destructive">{schemaError}</p>
                  )}
                </div>
              )}

              {/* New table name */}
              <div className="space-y-2">
                <Label htmlFor="target-table-name">New Table Name</Label>
                <form.Field name="targetTableName">
                  {(field) => (
                    <>
                      <Input
                        id="target-table-name"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="Enter table name"
                        data-testid="clone-target-table-name"
                      />
                      {tableNameError && (
                        <p className="text-xs text-destructive">
                          {tableNameError}
                        </p>
                      )}
                    </>
                  )}
                </form.Field>
              </div>
            </div>

            {/* Right column - Clone options */}
            <div className="space-y-4">
              <Label>Clone Options</Label>

              {/* Include data checkbox */}
              <form.Field name="includeData">
                {(field) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include-data"
                      checked={field.state.value}
                      onCheckedChange={(checked) =>
                        field.handleChange(checked === true)
                      }
                      data-testid="clone-include-data"
                    />
                    <Label
                      htmlFor="include-data"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Include data (copy all rows)
                    </Label>
                  </div>
                )}
              </form.Field>

              {/* Include constraints checkbox */}
              <form.Field name="includeConstraints">
                {(field) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include-constraints"
                      checked={field.state.value}
                      onCheckedChange={(checked) =>
                        field.handleChange(checked === true)
                      }
                      data-testid="clone-include-constraints"
                    />
                    <Label
                      htmlFor="include-constraints"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Include indexes and constraints
                    </Label>
                  </div>
                )}
              </form.Field>

              {/* Constraint sub-options */}
              {includeConstraints && (
                <div className="space-y-2 p-3 bg-muted/50 rounded-md">
                  <form.Field name="constraintOptions.primaryKey">
                    {(field) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="pk"
                          checked={field.state.value}
                          onCheckedChange={(checked) =>
                            field.handleChange(checked === true)
                          }
                          data-testid="clone-pk"
                        />
                        <Label htmlFor="pk" className="text-sm font-normal cursor-pointer">
                          Primary Key
                        </Label>
                      </div>
                    )}
                  </form.Field>
                  <form.Field name="constraintOptions.foreignKeys">
                    {(field) => (
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="fk"
                            checked={field.state.value}
                            onCheckedChange={(checked) =>
                              field.handleChange(checked === true)
                            }
                            data-testid="clone-fk"
                          />
                          <Label htmlFor="fk" className="text-sm font-normal cursor-pointer">
                            Foreign Keys
                          </Label>
                        </div>
                        {isCrossDatabase && field.state.value && (
                          <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-amber-700 dark:text-amber-400">
                            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <p className="text-xs">
                              Foreign keys may fail if referenced tables don&apos;t exist.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </form.Field>
                  <form.Field name="constraintOptions.uniqueConstraints">
                    {(field) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="unique"
                          checked={field.state.value}
                          onCheckedChange={(checked) =>
                            field.handleChange(checked === true)
                          }
                          data-testid="clone-unique"
                        />
                        <Label htmlFor="unique" className="text-sm font-normal cursor-pointer">
                          Unique Constraints
                        </Label>
                      </div>
                    )}
                  </form.Field>
                  <form.Field name="constraintOptions.checkConstraints">
                    {(field) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="check"
                          checked={field.state.value}
                          onCheckedChange={(checked) =>
                            field.handleChange(checked === true)
                          }
                          data-testid="clone-check"
                        />
                        <Label htmlFor="check" className="text-sm font-normal cursor-pointer">
                          Check Constraints
                        </Label>
                      </div>
                    )}
                  </form.Field>
                  <form.Field name="constraintOptions.indexes">
                    {(field) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="indexes"
                          checked={field.state.value}
                          onCheckedChange={(checked) =>
                            field.handleChange(checked === true)
                          }
                          data-testid="clone-indexes"
                        />
                        <Label htmlFor="indexes" className="text-sm font-normal cursor-pointer">
                          Indexes
                        </Label>
                      </div>
                    )}
                  </form.Field>
                </div>
              )}
            </div>
          </div>
        </form>

              <DialogFooter className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePreview}
                  disabled={isCloning || !isFormValid}
                  data-testid="clone-preview-button"
                >
                  Preview Script
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isCloning}
                    data-testid="clone-cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    form="clone-table-form"
                    disabled={isCloning || !isFormValid}
                    data-testid="clone-submit-button"
                  >
                    {isCloning ? 'Cloning...' : 'Clone'}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Generated-DDL preview */}
      <CloneScriptPreviewDialog
        open={previewState.open}
        onOpenChange={setPreviewOpen}
        targetTableName={targetTableName}
        script={previewState.script}
        error={previewState.error}
        isLoading={previewState.isLoading}
      />

      {/* Upgrade dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />
    </>
  );
}
